/**
 * Column width bounds + char-estimate auto-fit for the tabular grid.
 * Same clamp bounds used by both manual resize and auto-fit-to-content.
 */

export const MIN_COL_WIDTH = 72;
export const MAX_COL_WIDTH = 480;
/** Trailing row-action column — stays in the shared horizontal scroll. */
export const ACTION_COL_WIDTH = 40;

/** Floor width so header/body/footer share one horizontal scroll when columns overflow. */
export function tabularMinWidth(
  colNames: string[],
  widths: Record<string, number>,
): number {
  return colNames.reduce(
    (sum, name) => sum + (widths[name] ?? MIN_COL_WIDTH),
    ACTION_COL_WIDTH,
  )
}

export type AutoFitKind = 'text' | 'number';

export function clampColWidth(w: number): number {
  const safe = Number.isFinite(w) ? w : MIN_COL_WIDTH;
  return Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, safe));
}

/**
 * Estimates a pixel width from the longest of the header and sample cell
 * values, using a fixed per-character estimate (no DOM measurement).
 * Number kind adds cell padding (16+12) after the char estimate, then
 * floors at minWidth, then clamps.
 */
export function autoFitWidth(
  header: string,
  cellSamples: string[],
  opts?: { kind?: AutoFitKind; minWidth?: number },
): number {
  const candidates = [header, ...cellSamples].filter((s): s is string => typeof s === 'string');
  const longest = candidates.reduce((max, s) => Math.max(max, s.length), 0);
  let px = longest * 8 + 24;
  if (opts?.kind === 'number') px += 16 + 12;
  if (opts?.minWidth) px = Math.max(px, opts.minWidth);
  return clampColWidth(px);
}
